import React, { useEffect, useState } from "react";
import { ChevronDown, Save, Plus, X, ExternalLink, RotateCcw, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DEFAULT_MARKETING, useMarketing } from "../lib/marketingContent";

/**
 * AdminMarketing — super-admin (Erika only) editor for the SaaS marketing
 * landing page (theporchlight.io). Each section has its own draft state and
 * Save button; clicking save patches just that section, then refreshes the
 * MarketingProvider so the public landing picks up the change on its next
 * load.
 *
 * Defensive UI: if the current user isn't the primary admin, we 403-redirect
 * them. Backend also enforces _require_primary_admin on PUT.
 */
export default function AdminMarketing() {
  const { user } = useAuth();
  const { content, overrides, refresh } = useMarketing();
  const [openSection, setOpenSection] = useState("hero");

  // Primary-admin gate (server enforces; this is just UX).
  if (user && !user.is_primary_admin) {
    return (
      <div className="container-x" style={{ padding: "64px 0" }} data-testid="admin-marketing-403">
        <h1 className="font-serif" style={{ fontSize: 32 }}>Not authorized</h1>
        <p style={{ color: "var(--text-muted)" }}>This editor is reserved for the platform owner.</p>
      </div>
    );
  }

  const sections = [
    { id: "hero",            label: "Hero",                    Component: HeroSection },
    { id: "built_by_agent",  label: "Top ribbon",              Component: BuiltByAgentSection },
    { id: "sound_familiar",  label: "Sound familiar?",         Component: SoundFamiliarSection },
    { id: "category",        label: "Category positioning",    Component: CategorySection },
    { id: "demo_video",      label: "Demo video",              Component: DemoVideoSection },
    { id: "what_it_does",    label: "What it does",            Component: WhatItDoesSection },
    { id: "spotlights",      label: "Feature spotlights (4)",  Component: SpotlightsSection },
    { id: "more_features",   label: "“And a whole lot more”",  Component: MoreFeaturesSection },
    { id: "why_agents",      label: "Why agents use it",       Component: WhyAgentsSection },
    { id: "pricing",         label: "Pricing",                 Component: PricingSection },
    { id: "faq",             label: "FAQ",                     Component: FaqSection },
    { id: "waitlist",        label: "Waitlist form",           Component: WaitlistSection },
    { id: "footer",          label: "Footer",                  Component: FooterSection },
  ];

  return (
    <div className="container-x" style={{ padding: "32px 0 96px" }} data-testid="admin-marketing-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 28 }}>
        <div>
          <span className="overline">Super-admin</span>
          <h1 className="font-serif" style={{ fontSize: "clamp(28px, 4vw, 40px)", margin: "6px 0 6px", fontWeight: 500, letterSpacing: "-0.01em" }}>
            Marketing page editor
          </h1>
          <p style={{ color: "var(--text-muted)", maxWidth: 640 }}>
            Edit any section of the public marketing site at theporchlight.io.
            Changes save independently per section. Open the live page in a new tab and refresh to verify.
          </p>
        </div>
        <a
          data-testid="admin-marketing-preview-link"
          href="/for-agents"
          target="_blank"
          rel="noreferrer"
          className="btn-secondary"
          style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          Open marketing page <ExternalLink size={14} />
        </a>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {sections.map(({ id, label, Component }) => {
          const isOpen = openSection === id;
          const hasOverride = overrides[id] != null;
          return (
            <div key={id} className="card" data-testid={`marketing-section-${id}`} style={{ overflow: "hidden" }}>
              <button
                type="button"
                data-testid={`marketing-section-toggle-${id}`}
                onClick={() => setOpenSection(isOpen ? null : id)}
                style={{
                  width: "100%", textAlign: "left", padding: "18px 22px",
                  background: isOpen ? "var(--surface-alt)" : "#fff",
                  border: "none", borderBottom: isOpen ? "1px solid var(--border)" : "none",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
                  <span className="font-serif" style={{ fontSize: 18, fontWeight: 500 }}>{label}</span>
                  {hasOverride && (
                    <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 999, background: "var(--secondary)", color: "#fff", letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 600 }}>
                      Customized
                    </span>
                  )}
                </span>
                <ChevronDown size={18} style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.18s ease", color: "var(--text-muted)" }} />
              </button>
              {isOpen && (
                <div style={{ padding: 24 }}>
                  <Component
                    sectionId={id}
                    initial={content[id]}
                    hasOverride={hasOverride}
                    onSaved={refresh}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Reusable form primitives
   ============================================================ */

function TextField({ label, value, onChange, multiline, rows = 3, hint, testid }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }} data-testid={testid}>
      <span style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6, color: "var(--text)" }}>{label}</span>
      {multiline ? (
        <textarea
          className="input-field"
          rows={rows}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          style={{ resize: "vertical", fontFamily: "inherit" }}
        />
      ) : (
        <input
          className="input-field"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {hint && <div style={{ fontSize: 12, color: "var(--text-faint)", marginTop: 4 }}>{hint}</div>}
    </label>
  );
}

function StringList({ label, items, onChange, placeholder, testid }) {
  const setAt = (i, v) => onChange(items.map((it, j) => (i === j ? v : it)));
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const add = () => onChange([...items, ""]);
  return (
    <div style={{ marginBottom: 14 }} data-testid={testid}>
      <span style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6, color: "var(--text)" }}>{label}</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {(items || []).map((it, i) => (
          <div key={i} style={{ display: "flex", gap: 8 }}>
            <input
              className="input-field"
              style={{ flex: 1 }}
              value={it}
              onChange={(e) => setAt(i, e.target.value)}
              placeholder={placeholder}
            />
            <button type="button" onClick={() => remove(i)} className="btn-ghost" style={{ padding: "8px 10px" }} aria-label="Remove">
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="btn-ghost" style={{ marginTop: 8, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}>
        <Plus size={14} /> Add
      </button>
    </div>
  );
}

function ObjectListEditor({ label, items, onChange, fields, testid }) {
  // fields: [{key, label, multiline?}]
  const setAt = (i, patch) => onChange(items.map((it, j) => (i === j ? { ...it, ...patch } : it)));
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const add = () => {
    const empty = fields.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});
    onChange([...items, empty]);
  };
  return (
    <div style={{ marginBottom: 14 }} data-testid={testid}>
      <span style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6, color: "var(--text)" }}>{label}</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {(items || []).map((it, i) => (
          <div key={i} style={{ background: "var(--surface-alt)", padding: 14, borderRadius: 10, border: "1px solid var(--border)" }}>
            {fields.map((f) => (
              <TextField
                key={f.key}
                label={f.label}
                value={it[f.key]}
                multiline={f.multiline}
                rows={f.rows}
                onChange={(v) => setAt(i, { [f.key]: v })}
              />
            ))}
            <button type="button" onClick={() => remove(i)} className="btn-ghost" style={{ fontSize: 13, color: "var(--secondary)" }}>
              <X size={14} /> Remove
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="btn-ghost" style={{ marginTop: 8, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}>
        <Plus size={14} /> Add
      </button>
    </div>
  );
}

/**
 * useSectionDraft — owns the draft + save lifecycle for one section.
 * Saves a single-key patch ({ [sectionId]: draft }) so unrelated sections
 * are never overwritten. Calls onSaved() on success so the provider refreshes.
 */
function useSectionDraft({ sectionId, initial, hasOverride, onSaved }) {
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ kind: "idle", msg: "" });

  // Re-sync draft when the upstream content changes (e.g. another save just landed)
  useEffect(() => { setDraft(initial); }, [initial]);

  const save = async () => {
    setBusy(true);
    setStatus({ kind: "idle", msg: "" });
    try {
      await api.put("/admin/marketing-content", { [sectionId]: draft });
      await onSaved();
      setStatus({ kind: "ok", msg: "Saved." });
      setTimeout(() => setStatus({ kind: "idle", msg: "" }), 2200);
    } catch (e) {
      setStatus({ kind: "err", msg: e?.response?.data?.detail || "Save failed. Try again." });
    } finally {
      setBusy(false);
    }
  };

  const resetToDefaults = async () => {
    if (!confirm("Revert this section to the built-in default copy? Your custom text will be lost.")) return;
    setBusy(true);
    try {
      // Save `null` for this section so the override is removed; backend stores
      // null which our merge treats as "use defaults".
      await api.put("/admin/marketing-content", { [sectionId]: null });
      await onSaved();
      // Re-set draft from defaults so the form reflects what's now showing.
      setDraft(DEFAULT_MARKETING[sectionId]);
      setStatus({ kind: "ok", msg: "Reverted to default." });
      setTimeout(() => setStatus({ kind: "idle", msg: "" }), 2200);
    } catch (e) {
      setStatus({ kind: "err", msg: "Couldn't revert. Try again." });
    } finally {
      setBusy(false);
    }
  };

  return { draft, setDraft, busy, status, save, resetToDefaults, hasOverride };
}

function SaveBar({ ctx }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginTop: 8, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
      <div style={{ fontSize: 13, color: ctx.status.kind === "err" ? "var(--secondary)" : ctx.status.kind === "ok" ? "var(--primary)" : "var(--text-faint)", minHeight: 20 }}>
        {ctx.status.msg}
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        {ctx.hasOverride && (
          <button
            type="button"
            data-testid="section-revert-btn"
            onClick={ctx.resetToDefaults}
            className="btn-ghost"
            disabled={ctx.busy}
            style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, color: "var(--text-muted)" }}
          >
            <RotateCcw size={13} /> Revert to default
          </button>
        )}
        <button
          type="button"
          data-testid="section-save-btn"
          onClick={ctx.save}
          className="btn-primary"
          disabled={ctx.busy}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          {ctx.busy ? <><Loader2 size={14} className="spin" /> Saving…</> : <><Save size={14} /> Save</>}
        </button>
      </div>
    </div>
  );
}

/* ============================================================
   Section editors
   ============================================================ */

function HeroSection(props) {
  const ctx = useSectionDraft(props);
  const d = ctx.draft || {};
  const set = (k, v) => ctx.setDraft({ ...d, [k]: v });
  return (
    <>
      <TextField label="Eyebrow / overline" value={d.overline} onChange={(v) => set("overline", v)} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <TextField label="Headline (regular text)" value={d.headline} onChange={(v) => set("headline", v)} />
        <TextField label="Headline italic accent" value={d.headline_italic} onChange={(v) => set("headline_italic", v)} hint="Renders italic + colored." />
      </div>
      <TextField label="Subhead" value={d.subhead} onChange={(v) => set("subhead", v)} />
      <TextField label="Body" multiline rows={3} value={d.body} onChange={(v) => set("body", v)} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <TextField label="Primary CTA label" value={d.cta_primary} onChange={(v) => set("cta_primary", v)} />
        <TextField label="Secondary CTA label" value={d.cta_secondary} onChange={(v) => set("cta_secondary", v)} />
      </div>
      <TextField label="Social proof line" value={d.social_proof} onChange={(v) => set("social_proof", v)} />
      <SaveBar ctx={ctx} />
    </>
  );
}

function BuiltByAgentSection(props) {
  const ctx = useSectionDraft(props);
  return (
    <>
      <ObjectListEditor
        label="Ribbon items (3 recommended)"
        items={ctx.draft || []}
        onChange={ctx.setDraft}
        fields={[
          { key: "stat", label: "Setup phrase (regular)" },
          { key: "emphasis", label: "Italic emphasis" },
        ]}
      />
      <SaveBar ctx={ctx} />
    </>
  );
}

function SoundFamiliarSection(props) {
  const ctx = useSectionDraft(props);
  const d = ctx.draft || {};
  const set = (k, v) => ctx.setDraft({ ...d, [k]: v });
  return (
    <>
      <TextField label="Eyebrow" value={d.overline} onChange={(v) => set("overline", v)} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <TextField label="Headline (regular)" value={d.headline} onChange={(v) => set("headline", v)} />
        <TextField label="Headline italic accent" value={d.headline_italic} onChange={(v) => set("headline_italic", v)} />
      </div>
      <TextField label="Body" multiline value={d.body} onChange={(v) => set("body", v)} />
      <StringList label="Chaos column (without PorchLight)" items={d.chaos || []} onChange={(v) => set("chaos", v)} />
      <StringList label="Calm column (with PorchLight)" items={d.calm || []} onChange={(v) => set("calm", v)} />
      <SaveBar ctx={ctx} />
    </>
  );
}

function CategorySection(props) {
  const ctx = useSectionDraft(props);
  const d = ctx.draft || {};
  const set = (k, v) => ctx.setDraft({ ...d, [k]: v });
  return (
    <>
      <TextField label="Eyebrow" value={d.overline} onChange={(v) => set("overline", v)} />
      <TextField label="Eyebrow line" value={d.eyebrow} onChange={(v) => set("eyebrow", v)} hint="E.g. “PorchLight isn't another CRM.”" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <TextField label="Headline (regular)" value={d.headline} onChange={(v) => set("headline", v)} />
        <TextField label="Headline emphasis" value={d.headline_emphasis} onChange={(v) => set("headline_emphasis", v)} />
      </div>
      <TextField label="Body" multiline value={d.body} onChange={(v) => set("body", v)} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <TextField label="NOT label" value={d.not_label} onChange={(v) => set("not_label", v)} />
        <TextField label="IS label" value={d.is_label} onChange={(v) => set("is_label", v)} />
      </div>
      <StringList label="NOT items" items={d.not_items || []} onChange={(v) => set("not_items", v)} />
      <TextField label="NOT footnote" value={d.not_footnote} onChange={(v) => set("not_footnote", v)} />
      <StringList label="IS items" items={d.is_items || []} onChange={(v) => set("is_items", v)} />
      <TextField label="IS footnote" value={d.is_footnote} onChange={(v) => set("is_footnote", v)} />
      <SaveBar ctx={ctx} />
    </>
  );
}

function DemoVideoSection(props) {
  const ctx = useSectionDraft(props);
  const d = ctx.draft || {};
  const set = (k, v) => ctx.setDraft({ ...d, [k]: v });
  return (
    <>
      <TextField label="Eyebrow" value={d.overline} onChange={(v) => set("overline", v)} />
      <TextField label="Headline" value={d.headline} onChange={(v) => set("headline", v)} />
      <TextField label="Body" multiline value={d.body} onChange={(v) => set("body", v)} />
      <TextField label="Badge under play button" value={d.badge} onChange={(v) => set("badge", v)} />
      <TextField label="CTA link text" value={d.cta} onChange={(v) => set("cta", v)} />
      <SaveBar ctx={ctx} />
    </>
  );
}

function WhatItDoesSection(props) {
  const ctx = useSectionDraft(props);
  const d = ctx.draft || {};
  const set = (k, v) => ctx.setDraft({ ...d, [k]: v });
  return (
    <>
      <TextField label="Eyebrow" value={d.overline} onChange={(v) => set("overline", v)} />
      <TextField label="Headline" value={d.headline} onChange={(v) => set("headline", v)} />
      <TextField label="Body" multiline value={d.body} onChange={(v) => set("body", v)} />
      <StringList label="List items (4 recommended)" items={d.items || []} onChange={(v) => set("items", v)} />
      <SaveBar ctx={ctx} />
    </>
  );
}

function SpotlightsSection(props) {
  const ctx = useSectionDraft(props);
  return (
    <>
      <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 14 }}>
        Each spotlight has its own screenshots, defined in code. Edit the text below — visual layouts stay as-is.
      </p>
      <ObjectListEditor
        label="Spotlights"
        items={ctx.draft || []}
        onChange={ctx.setDraft}
        fields={[
          { key: "id", label: "ID (must match: prep-guide-spotlight, net-sheet-spotlight, important-dates-spotlight, agent-portal-spotlight)" },
          { key: "overline", label: "Eyebrow" },
          { key: "headline", label: "Headline (full)" },
          { key: "italic_tail", label: "Italic tail (last words to italicize, must end the headline)" },
          { key: "body", label: "Body", multiline: true, rows: 3 },
        ]}
      />
      <p style={{ fontSize: 12, color: "var(--text-faint)", marginTop: -4, marginBottom: 14 }}>
        Tip: edit each spotlight's bullet points by saving and then editing them inline below in a future release. For now, body text + headline cover most needs.
      </p>
      <SaveBar ctx={ctx} />
    </>
  );
}

function MoreFeaturesSection(props) {
  const ctx = useSectionDraft(props);
  const d = ctx.draft || {};
  const set = (k, v) => ctx.setDraft({ ...d, [k]: v });
  return (
    <>
      <TextField label="Eyebrow" value={d.overline} onChange={(v) => set("overline", v)} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <TextField label="Headline (regular)" value={d.headline} onChange={(v) => set("headline", v)} />
        <TextField label="Headline italic accent" value={d.headline_italic} onChange={(v) => set("headline_italic", v)} />
      </div>
      <TextField label="Body" multiline value={d.body} onChange={(v) => set("body", v)} />
      <ObjectListEditor
        label="Features grid (icons match by position; up to 9 to keep visual layout)"
        items={d.items || []}
        onChange={(v) => set("items", v)}
        fields={[
          { key: "title", label: "Title" },
          { key: "body", label: "Body", multiline: true, rows: 2 },
        ]}
      />
      <SaveBar ctx={ctx} />
    </>
  );
}

function WhyAgentsSection(props) {
  const ctx = useSectionDraft(props);
  const d = ctx.draft || {};
  const set = (k, v) => ctx.setDraft({ ...d, [k]: v });
  return (
    <>
      <TextField label="Eyebrow" value={d.overline} onChange={(v) => set("overline", v)} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <TextField label="Headline (regular)" value={d.headline} onChange={(v) => set("headline", v)} />
        <TextField label="Headline italic accent" value={d.headline_italic} onChange={(v) => set("headline_italic", v)} />
      </div>
      <StringList label="Reasons (6 recommended for the 2-column grid)" items={d.reasons || []} onChange={(v) => set("reasons", v)} />
      <SaveBar ctx={ctx} />
    </>
  );
}

function PricingSection(props) {
  const ctx = useSectionDraft(props);
  const d = ctx.draft || {};
  const set = (k, v) => ctx.setDraft({ ...d, [k]: v });
  return (
    <>
      <TextField label="Eyebrow" value={d.overline} onChange={(v) => set("overline", v)} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <TextField label="Headline (regular)" value={d.headline} onChange={(v) => set("headline", v)} />
        <TextField label="Headline italic accent" value={d.headline_italic} onChange={(v) => set("headline_italic", v)} />
      </div>
      <TextField label="Body (use line breaks freely)" multiline value={d.body} onChange={(v) => set("body", v)} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <StringList label="Left feature column" items={d.features_left || []} onChange={(v) => set("features_left", v)} />
        <StringList label="Right feature column" items={d.features_right || []} onChange={(v) => set("features_right", v)} />
      </div>
      <TextField label="CTA label" value={d.cta} onChange={(v) => set("cta", v)} />
      <SaveBar ctx={ctx} />
    </>
  );
}

function FaqSection(props) {
  const ctx = useSectionDraft(props);
  const d = ctx.draft || {};
  const set = (k, v) => ctx.setDraft({ ...d, [k]: v });
  return (
    <>
      <TextField label="Eyebrow" value={d.overline} onChange={(v) => set("overline", v)} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <TextField label="Headline (regular)" value={d.headline} onChange={(v) => set("headline", v)} />
        <TextField label="Headline italic accent" value={d.headline_italic} onChange={(v) => set("headline_italic", v)} />
      </div>
      <TextField label="Side body" multiline value={d.body} onChange={(v) => set("body", v)} />
      <TextField label="CTA link text" value={d.cta} onChange={(v) => set("cta", v)} />
      <ObjectListEditor
        label="Q&A items"
        items={d.items || []}
        onChange={(v) => set("items", v)}
        fields={[
          { key: "q", label: "Question" },
          { key: "a", label: "Answer", multiline: true, rows: 4 },
        ]}
      />
      <SaveBar ctx={ctx} />
    </>
  );
}

function WaitlistSection(props) {
  const ctx = useSectionDraft(props);
  const d = ctx.draft || {};
  const set = (k, v) => ctx.setDraft({ ...d, [k]: v });
  return (
    <>
      <TextField label="Eyebrow" value={d.overline} onChange={(v) => set("overline", v)} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <TextField label="Headline (regular)" value={d.headline} onChange={(v) => set("headline", v)} />
        <TextField label="Headline italic accent" value={d.headline_italic} onChange={(v) => set("headline_italic", v)} />
      </div>
      <TextField label="Body" multiline value={d.body} onChange={(v) => set("body", v)} />
      <TextField label="Pain-point textarea placeholder" value={d.pain_placeholder} onChange={(v) => set("pain_placeholder", v)} />
      <TextField label="Submit button label" value={d.submit_label} onChange={(v) => set("submit_label", v)} />
      <TextField label="Privacy note" value={d.privacy_note} onChange={(v) => set("privacy_note", v)} />
      <TextField label="Success headline" value={d.success_headline} onChange={(v) => set("success_headline", v)} />
      <TextField label="Success body" multiline value={d.success_body} onChange={(v) => set("success_body", v)} />
      <SaveBar ctx={ctx} />
    </>
  );
}

function FooterSection(props) {
  const ctx = useSectionDraft(props);
  const d = ctx.draft || {};
  const set = (k, v) => ctx.setDraft({ ...d, [k]: v });
  return (
    <>
      <TextField label="Brand tagline (also used in copyright line)" value={d.tagline} onChange={(v) => set("tagline", v)} />
      <SaveBar ctx={ctx} />
    </>
  );
}
